import isNumber from 'lodash/isNumber'
import { Check, ChevronsUpDown, LockKeyholeIcon } from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ProcessResourceFallback } from '@/components/form/process-resource-selector/fallback'
import { ProcessBadge } from '@/components/sv/process-badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  RuntimeProcessTypesQuery,
  RuntimeProcessTypesQueryVariables,
} from '@/components/form/process-resource-selector/index.types.gen'
import type { ProcessResourceCategory } from '@/graphql.types'

const categoryMap = {
  hobby: 'Hobby',
  standard: 'Standard',
  cpuOptimized: 'CPU Optimized',
  memoryOptimized: 'Memory Optimized',
}

const RUNTIME_PROCESS_QUERY = gql`
  query RuntimeProcessTypes($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        taxPercent
      }
      defaultPaymentMethod {
        id
        expYear
        expMonth
      }
      processResourceTypes {
        id
        name
        category
        cpuLimitDisplayName
        cpuLimit
        memoryLimit
        price {
          unitNetPrice
        }
      }
    }
  }
`

type Props = {
  fieldKey?: string
  variant?: 'default' | 'compact'
  interval?: 'second' | 'minute' | 'hour' | 'month'
  shouldFilterH1?: boolean
  processType?: 'web' | 'worker' | 'cron' | 'job'
}

const intervalConfig = {
  second: {
    factor: 1,
    round: 100_000_000,
    label: 'second',
  },
  minute: {
    factor: 60,
    round: 10_000_000,
    label: 'minute',
  },
  hour: {
    factor: 60 * 60,
    round: 1_000_000,
    label: 'hour',
  },
  month: {
    factor: 60 * 60 * 730,
    round: 1_000_000,
    label: 'month',
  },
}

type ProcessResourceType = NonNullable<
  NonNullable<RuntimeProcessTypesQuery['company']>['processResourceTypes']
>[number]

export const ProcessResourceSelectorContent = ({
  fieldKey = 'idProcessResourceType',
  variant = 'default',
  interval = 'month',
  shouldFilterH1 = false,
  processType,
}: Props) => {
  const [open, setOpen] = useState(false)
  const { idCompany } = useIdCompany()
  const form = useFormContext()
  const { watch } = form

  const idProcessResourceType = watch(fieldKey as any)

  const { data } = useSuspenseQuery<
    RuntimeProcessTypesQuery,
    RuntimeProcessTypesQueryVariables
  >(RUNTIME_PROCESS_QUERY, {
    variables: { idCompany },
  })
  const hasVerifiedCard = Boolean(data?.company?.defaultPaymentMethod?.id)
  const selectedProcessResourceType = data?.company?.processResourceTypes?.find(
    (resource) => resource?.id === idProcessResourceType
  )

  const taxPercent = data?.company?.sevallaBilling?.taxPercent ?? 0
  const categories: ProcessResourceCategory[] = [
    'hobby',
    'standard',
    'cpuOptimized',
    'memoryOptimized',
  ]
  const resourceCategories = categories
    .filter((category) => {
      if (shouldFilterH1 && processType === 'web' && category === 'hobby') {
        return false
      }
      return true
    })
    .map((category) => ({
      name: category,
      types:
        data?.company?.processResourceTypes?.filter((resource) => {
          if (resource?.category !== category) {
            return false
          }

          if (
            shouldFilterH1 &&
            processType === 'web' &&
            resource?.name === 'h1'
          ) {
            return false
          }

          return true
        }) ?? [],
    }))
    .sort((a, b) => {
      if (a.name === 'hobby') {
        return -1
      }
      if (b.name === 'hobby') {
        return 1
      }
      if (a.name === 'standard') {
        return -1
      }
      if (b.name === 'standard') {
        return 1
      }
      if (a.name === 'cpuOptimized') {
        return -1
      }
      if (b.name === 'cpuOptimized') {
        return 1
      }
      if (a.name === 'memoryOptimized') {
        return -1
      }
      if (b.name === 'memoryOptimized') {
        return 1
      }
      return 0
    })

  useEffect(() => {
    if (!idProcessResourceType && data?.company?.processResourceTypes?.length) {
      let defaultResource = data.company.processResourceTypes.find(
        (resource) => resource?.category === 'hobby'
      )

      if (
        shouldFilterH1 &&
        processType === 'web' &&
        defaultResource?.name === 'h1'
      ) {
        defaultResource = data.company.processResourceTypes.find(
          (resource) => resource?.name !== 'h1'
        )
      }

      if (defaultResource) {
        form.setValue(fieldKey as any, defaultResource.id)
      }
    }
  }, [data, idProcessResourceType, form, fieldKey, shouldFilterH1, processType])

  const renderResourceRow = (resource: ProcessResourceType) => {
    const config = intervalConfig[interval]
    const cpuLimit = (resource?.cpuLimit ?? 0) / 1000
    const netPrice = (resource?.price?.unitNetPrice ?? 0) * config.factor
    const grossPrice = netPrice * (1 + taxPercent / 100)
    const price =
      Math.round((grossPrice + Number.EPSILON) * config.round) / config.round
    const memoryLimit = (resource?.memoryLimit ?? 0) / 1000

    return (
      <div className="flex items-center w-full justify-between truncate">
        <div className="flex items-center gap-2">
          {!hasVerifiedCard && !resource?.name?.startsWith('h') ? (
            <LockKeyholeIcon className="size-3.5" />
          ) : null}
          <ProcessBadge
            category={resource?.category}
            name={resource?.name}
            className={
              !hasVerifiedCard && !resource?.name?.startsWith('h')
                ? 'opacity-50'
                : ''
            }
          />
          {cpuLimit} CPU / {memoryLimit} GB RAM
        </div>
        {isNumber(resource?.price?.unitNetPrice) && (
          <span
            className={
              !hasVerifiedCard && !resource?.name?.startsWith('h')
                ? 'opacity-50'
                : ''
            }
          >
            {price} USD / {config.label}
          </span>
        )}
      </div>
    )
  }

  return (
    <FormField
      control={form.control}
      name={fieldKey as any}
      render={({ field }) => (
        <FormItem className="w-full min-w-0">
          <FormLabel>Resource</FormLabel>
          <FormControl>
            <div className="flex flex-col gap-4 w-full min-w-0">
              <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent min-w-0"
                  >
                    {selectedProcessResourceType ? (
                      <div className="flex items-center justify-between gap-2 w-full min-w-0">
                        {renderResourceRow(selectedProcessResourceType)}
                        <ChevronsUpDown className="shrink-0 opacity-50" />
                      </div>
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        Select a resource type
                        <ChevronsUpDown className="ml-auto opacity-50" />
                      </div>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popper-anchor-width)] min-w-[400px] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search resource..."
                      className="h-9"
                    />
                    <CommandList>
                      <CommandEmpty>No resource found</CommandEmpty>
                      {resourceCategories.map((group) => {
                        return (
                          <CommandGroup
                            key={group.name}
                            heading={categoryMap[group.name]}
                          >
                            {group.types.map((resource) => {
                              if (!resource) {
                                return null
                              }
                              const isSelected = field.value === resource.id
                              return (
                                <CommandItem
                                  className="cursor-pointer"
                                  key={resource.id}
                                  disabled={
                                    isSelected ||
                                    (!hasVerifiedCard && resource.name !== 'h1')
                                  }
                                  onSelect={() => {
                                    field.onChange(resource.id)
                                    setOpen(false)
                                  }}
                                >
                                  {renderResourceRow(resource)}
                                  <Check
                                    className={cn(
                                      'ml-auto',
                                      isSelected ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                </CommandItem>
                              )
                            })}
                          </CommandGroup>
                        )
                      })}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FormControl>
          {variant === 'default' ? (
            <FormDescription>
              Each application includes a default Web process where this
              resource is initially applied.
            </FormDescription>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export const ProcessResourceSelector = (props: Props) => {
  return (
    <Suspense fallback={<ProcessResourceFallback variant={props.variant} />}>
      <ProcessResourceSelectorContent {...props} />
    </Suspense>
  )
}
